import React, { useCallback, useContext } from 'react';
import { graphql, useFragment } from 'react-relay';
import {
	STAR,
	UNSTAR,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/star.tsx';
import { FavoriteToggleButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/favorite-toggle-button/index.tsx';
import { ReorderActionsRenderer } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/reorder-actions-renderer/ReorderActionsRenderer.tsx';
import type { OnCloseProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import Section from '@atlaskit/menu/section';
import { AnalyticContext } from './common/controllers/analytic-context/index.tsx';
import { useDragAndDropReorderingAnalytics } from './useDragAndDropReorderingAnalytics.tsx';
import { useRoadmapReorderingIds } from './common/utils/useRoadmapReorderingIds.tsx';
import type { RoadmapFavoriteSubmenu_collection$key } from './__generated__/RoadmapFavoriteSubmenu_collection.graphql';

type ReorderProps = {
	currentItems: Array<{ id: string; label: string }>;
	currentOrder: Array<{ menuId: string; visible: boolean }>;
	item: {
		id: string;
		label: string;
	};
	onClose: () => void;
};

/**
 * Inner component that renders the ReorderActionsRenderer.
 * Extracted to isolate the useRoadmapReorderingIds hook call,
 * so it only runs when reordering data is provided.
 */
const RoadmapReorderActions = ({
	currentItems,
	currentOrder,
	item,
	onClose,
}: ReorderProps): React.JSX.Element => {
	const { entityAri, ownerAri } = useRoadmapReorderingIds();
	const { fireAnalyticsEvent } = useDragAndDropReorderingAnalytics();

	return (
		<ReorderActionsRenderer
			currentItems={currentItems}
			currentOrder={currentOrder}
			entityAri={entityAri}
			ownerAri={ownerAri}
			fireAnalyticsEvent={fireAnalyticsEvent}
			item={item}
			onClose={onClose}
		/>
	);
};

type Props = {
	collectionQueryRefFragment: RoadmapFavoriteSubmenu_collection$key;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
	onClose?: OnCloseProps['onClose'];
};

export const RoadmapFavoriteSubmenu: React.FC<Props> = ({
	collectionQueryRefFragment,
	currentItems,
	currentOrder,
	item,
	onClose,
}: Props): React.JSX.Element => {
	const collectionFavouriteItem = useFragment<RoadmapFavoriteSubmenu_collection$key>(
		graphql`
			fragment RoadmapFavoriteSubmenu_collection on JiraProductDiscoveryCollection {
				id @required(action: THROW)
				collectionId @required(action: THROW)
				favouriteValue {
					isFavourite
				}
			}
		`,
		collectionQueryRefFragment,
	);

	const { fireUIAnalyticForFavouriteToggle } = useContext(AnalyticContext);
	const isFavourite = Boolean(collectionFavouriteItem?.favouriteValue?.isFavourite);

	const handleFavouriteClick = useCallback(() => {
		fireUIAnalyticForFavouriteToggle({
			starType: isFavourite ? UNSTAR : STAR,
			parentItemId: String(collectionFavouriteItem.collectionId),
		});
	}, [collectionFavouriteItem.collectionId, fireUIAnalyticForFavouriteToggle, isFavourite]);

	const handleClose = useCallback(() => {
		onClose?.(null);
	}, [onClose]);

	return (
		<Section isList>
			<FavoriteToggleButton
				entityId={collectionFavouriteItem.id}
				defaultIsStarred={isFavourite}
				entityTypeName="JiraProductDiscoveryCollection"
				onClick={handleFavouriteClick}
			/>
			{currentItems && currentOrder && item ? (
				<RoadmapReorderActions
					currentItems={currentItems}
					currentOrder={currentOrder}
					item={item}
					onClose={handleClose}
				/>
			) : null}
		</Section>
	);
};
